import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import apiService from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  MapPin,
  Phone,
  Mail,
  FileText,
  Upload,
  Shield,
  Zap,
  AlertCircle,
  Check,
  Clock,
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

interface PropertyDetails {
  id: string;
  name: string;
  address: string;
  city: string;
  postcode: string;
  propertyType: string;
  accountManager: string;
  image: string;
}

interface Document {
  id: string;
  name: string;
  category: string;
  uploadDate: string;
  expiryDate?: string;
  status: 'Valid' | 'Expiring Soon' | 'Expired';
}

interface ServiceRecord {
  id: string;
  orderNumber: string;
  service: string;
  date: string;
  status: string;
  amount: number;
}

const PropertyDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '', address_line1: '', address_line2: '', city: '', postcode: '', property_type: '', notes: '',
  });

  const fetchProperty = async () => {
    try {
      const response = await apiService.request(`/properties/${id}`, 'GET');
      if (response.success && response.data) {
        setRawData(response.data);
        setEditForm({
          name: response.data.name || '',
          address_line1: response.data.address_line1 || '',
          address_line2: response.data.address_line2 || '',
          city: response.data.city || '',
          postcode: response.data.postcode || '',
          property_type: response.data.property_type || '',
          notes: response.data.notes || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch property:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    fetchProperty();
  }, [id]);

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const response = await apiService.request(`/properties/${id}`, 'PUT', editForm);
      if (response.success) {
        toast({ title: 'Property updated' });
        setEditing(false);
        fetchProperty();
      } else {
        toast({ title: 'Failed to update property', description: response.message, variant: 'destructive' });
      }
    } catch (error: any) {
      toast({ title: 'Failed to update property', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const p = rawData || {};

  const property: PropertyDetails = {
    id: String(p.id || id || ''),
    name: p.property_name || p.name || p.address || '—',
    address: p.address || p.full_address || '—',
    city: p.city || p.town || '—',
    postcode: p.postcode || '—',
    propertyType: p.property_type || p.type || '—',
    accountManager: p.account_manager || p.manager_name || '—',
    image: p.image_url || '',
  };

  const documents: Document[] = (p.documents || []).map((d: any) => ({
    id: String(d.id),
    name: d.document_name || d.name || '—',
    category: d.category || d.document_type || '—',
    uploadDate: d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB', { dateStyle: 'medium' }) : '—',
    expiryDate: d.expiry_date ? new Date(d.expiry_date).toLocaleDateString('en-GB', { dateStyle: 'medium' }) : undefined,
    status: (['Valid', 'Expiring Soon', 'Expired'].includes(d.status) ? d.status : 'Valid') as Document['status'],
  }));

  const services: ServiceRecord[] = (p.service_history || p.bookings || []).map((s: any) => ({
    id: String(s.id),
    orderNumber: s.booking_reference || `#${s.id}`,
    service: s.service_name || '—',
    date: s.booking_date ? new Date(s.booking_date).toLocaleDateString('en-GB', { dateStyle: 'medium' }) : '—',
    status: (s.status || 'PENDING').toUpperCase(),
    amount: s.total_amount_pence ? s.total_amount_pence / 100 : s.amount ? Number(s.amount) : 0,
  }));

  // Pull from API. The detail endpoint now returns `stats: {lifetime_spend_gbp, orders_done, open_quotes, due_invoices}`.
  const apiStats = p.stats || {};
  const stats = [
    {
      label: 'TOTAL LIFETIME SPEND',
      value: '£' + Number(apiStats.lifetime_spend_gbp || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 }),
      color: 'text-[#00543b]',
    },
    { label: 'ORDERS DONE', value: String(apiStats.orders_done || 0).padStart(2, '0'), color: 'text-[#00543b]' },
    { label: 'OPEN QUOTE', value: String(apiStats.open_quotes || 0).padStart(2, '0'), color: 'text-[#00543b]' },
    { label: 'DUE INVOICES', value: String(apiStats.due_invoices || 0).padStart(2, '0'), color: (apiStats.due_invoices || 0) > 0 ? 'text-red-600' : 'text-gray-900' },
  ];

  // Compliance % = valid documents / total documents (0 docs → 0%)
  const compTotal = p.compliance?.total || 0;
  const compValid = p.compliance?.valid || 0;
  const compliancePct = compTotal > 0 ? Math.round((compValid / compTotal) * 100) : 0;

  // Owner / primary contact comes from the API's `owner` object (joined from users table)
  const owner = p.owner || {};

  return (
    <>
      <Helmet>
        <title>{property.name} | Virewire Property Services</title>
      </Helmet>

      <DashboardLayout>
        <div className="w-full">
          {/* Breadcrumb */}
          <div className="text-sm text-gray-600 mb-6">
            <button
              onClick={() => navigate('/properties')}
              className="hover:text-[#00543b]"
            >
              Properties
            </button>
            <span className="mx-2">›</span>
            <span className="text-gray-900 font-medium">{property.name}</span>
          </div>

          {/* Header */}
          <Card className="mb-8 border-gray-200 overflow-hidden">
            <div className="flex gap-6 p-6">
              <div className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">
                      {property.name}
                    </h1>
                    <div className="flex items-center gap-1 text-gray-600 mb-4">
                      <MapPin className="h-4 w-4" />
                      <span>{property.address}</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/dashboard/documents')}
                    className="bg-[#00543b] hover:bg-[#003d2d]"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </Button>
                </div>

                <div className="grid grid-cols-3 gap-8">
                  <div>
                    <p className="text-xs font-bold text-gray-600 uppercase mb-1">
                      Property Type
                    </p>
                    <p className="text-sm text-gray-900 font-semibold">
                      {property.propertyType}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-600 uppercase mb-1">
                      Account Manager
                    </p>
                    <p className="text-sm text-gray-900 font-semibold">
                      {property.accountManager}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate('/dashboard/request-service')}
                      className="bg-[#00543b] hover:bg-[#003d2d] flex-1"
                    >
                      Request Service
                    </Button>
                    <Button
                      variant="outline"
                      className="border-gray-300 flex-1"
                      onClick={() => {
                        setActiveTab('overview');
                        setEditing(true);
                      }}
                    >
                      Edit Property
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-8">
            <div className="flex gap-8">
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'documents', label: 'Documents' },
                { id: 'service-history', label: 'Service History' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-2 font-semibold border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'text-[#00543b] border-[#00543b]'
                      : 'text-gray-600 border-transparent hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Site Information */}
              <Card className="border-gray-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-bold text-gray-900">
                      Site Information
                    </h2>
                    {!editing && (
                      <button
                        onClick={() => setEditing(true)}
                        className="text-[#00543b] text-sm font-semibold hover:underline"
                      >
                        ✎ Edit Details
                      </button>
                    )}
                  </div>

                  {editing ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold text-gray-600 uppercase block mb-1">Property Name</label>
                          <input
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-gray-600 uppercase block mb-1">Property Type</label>
                          <input
                            value={editForm.property_type}
                            onChange={(e) => setEditForm({ ...editForm, property_type: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-gray-600 uppercase block mb-1">Street 1</label>
                          <input
                            value={editForm.address_line1}
                            onChange={(e) => setEditForm({ ...editForm, address_line1: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-gray-600 uppercase block mb-1">Street 2</label>
                          <input
                            value={editForm.address_line2}
                            onChange={(e) => setEditForm({ ...editForm, address_line2: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-gray-600 uppercase block mb-1">City</label>
                          <input
                            value={editForm.city}
                            onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-gray-600 uppercase block mb-1">Postcode</label>
                          <input
                            value={editForm.postcode}
                            onChange={(e) => setEditForm({ ...editForm, postcode: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-gray-600 uppercase block mb-1">Notes / Access Instructions</label>
                        <textarea
                          value={editForm.notes}
                          onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none h-24"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button onClick={handleSaveEdit} disabled={saving} className="bg-[#00543b] hover:bg-[#003d2d]">
                          {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                        <Button variant="outline" className="border-gray-300" onClick={() => setEditing(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-8">
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase mb-2">
                          Primary Contact
                        </p>
                        <p className="text-gray-900 font-semibold">{owner.name || '—'}</p>
                        {owner.email && <p className="text-sm text-gray-600">{owner.email}</p>}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase mb-2">
                          Property Type
                        </p>
                        <p className="text-gray-900 font-semibold">{property.propertyType || '—'}</p>
                        {p.bedrooms || p.bathrooms || p.size_sqft ? (
                          <p className="text-sm text-gray-600">
                            {[p.bedrooms ? `${p.bedrooms} bed` : null,
                              p.bathrooms ? `${p.bathrooms} bath` : null,
                              p.size_sqft ? `${p.size_sqft} sqft` : null].filter(Boolean).join(' · ')}
                          </p>
                        ) : null}
                      </div>

                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase mb-2">
                          Mobile
                        </p>
                        <p className="text-gray-900 font-semibold">{owner.phone || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase mb-2">
                          Postcode
                        </p>
                        <p className="text-gray-900 font-semibold">{property.postcode || '—'}</p>
                        {property.city && property.city !== '—' && (
                          <p className="text-sm text-gray-600">{property.city}</p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Access Instructions */}
              {p.notes && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-bold text-orange-900 mb-1">
                        ACCESS INSTRUCTIONS
                      </h3>
                      <p className="text-sm text-orange-800 whitespace-pre-line">{p.notes}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-6">
                {/* Quick Stats */}
                <Card className="bg-[#00543b] border-0">
                  <CardContent className="p-6 text-white">
                    <h3 className="text-lg font-bold mb-6">Quick Stats</h3>
                    <div className="space-y-4">
                      {stats.map((stat, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span className="text-yellow-100 text-xs font-bold">
                            {stat.label}
                          </span>
                          <span className="text-2xl font-bold text-white">
                            {stat.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Map */}
                <Card className="border-gray-200 bg-gradient-to-br from-green-50 to-blue-50 overflow-hidden">
                  <CardContent className="p-0 h-full flex flex-col items-center justify-center min-h-[300px]">
                    <div className="text-center">
                      <MapPin className="h-12 w-12 text-[#00543b] mx-auto mb-4 opacity-30" />
                      <Button
                        variant="outline"
                        className="border-[#00543b] text-[#00543b]"
                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.address + ' ' + property.postcode)}`, '_blank')}
                      >
                        Map & Satellite View
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Virewire Managed Section */}
              <Card className="border-gray-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Check className="h-6 w-6 text-[#00543b] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">
                        Virewire Managed
                      </h3>
                      <p className="text-sm text-gray-600">
                        We handle the coordination, insurance verification, and payment processing for all vendors on this property. The platform manages this on your behalf for total peace of mind.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Compliance Section */}
              <Card className="border-gray-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-900 mb-4">
                    Your Documents, Perfectly Curated.
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    We are actively monitoring the expiration dates of your compliance certificates. You will receive an automated alert 60 days before any document expires.
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">
                      COMPLIANCE STATUS
                    </p>
                    <span className={`text-2xl font-bold ${compliancePct >= 80 ? 'text-[#00543b]' : compliancePct >= 50 ? 'text-orange-600' : 'text-red-600'}`}>
                      {compliancePct}% Secure
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              {/* Insurance */}
              <div>
                <h3 className="font-bold text-gray-900 mb-4">Insurance</h3>
                {documents
                  .filter((d) => d.category === 'Insurance')
                  .map((doc) => (
                    <Card key={doc.id} className="border-gray-200 mb-3">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <FileText className="h-8 w-8 text-gray-400" />
                          <div>
                            <p className="font-semibold text-gray-900">
                              {doc.name}
                            </p>
                            <p className="text-sm text-gray-600">
                              Uploaded {doc.uploadDate}
                            </p>
                          </div>
                        </div>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                          <Check className="h-3 w-3 mr-1" />
                          Valid
                        </span>
                      </CardContent>
                    </Card>
                  ))}
              </div>

              {/* Safety & Compliance */}
              <div>
                <h3 className="font-bold text-gray-900 mb-4">
                  Safety & Compliance
                </h3>
                {documents
                  .filter((d) => d.category === 'Safety & Compliance')
                  .map((doc) => (
                    <Card key={doc.id} className="border-gray-200 mb-3">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Shield className="h-8 w-8 text-gray-400" />
                          <div>
                            <p className="font-semibold text-gray-900">
                              {doc.name}
                            </p>
                            <p className="text-sm text-gray-600">
                              {doc.uploadDate}
                              {doc.expiryDate && ` • Expiry ${doc.expiryDate}`}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                            doc.status === 'Expiring Soon'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {doc.status === 'Expiring Soon' ? (
                            <>
                              <Clock className="h-3 w-3 mr-1" />
                              Expiring Soon
                            </>
                          ) : (
                            <>
                              <Check className="h-3 w-3 mr-1" />
                              Valid
                            </>
                          )}
                        </span>
                      </CardContent>
                    </Card>
                  ))}
              </div>

              {/* Agreements */}
              <div>
                <h3 className="font-bold text-gray-900 mb-4">Agreements</h3>
                <Card className="border-gray-200">
                  <CardContent className="p-12 text-center">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="font-semibold text-gray-900 mb-1">
                      No legal agreements uploaded yet
                    </p>
                    <p className="text-sm text-gray-600 mb-4">
                      We keep your lease and management agreements in one secure, digital vault.
                    </p>
                    <Button
                      variant="outline"
                      className="text-[#00543b] border-[#00543b]"
                      onClick={() => navigate('/dashboard/documents')}
                    >
                      Add First Agreement
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Compliance Section */}
              <Card className="border-gray-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-900 mb-4">
                    Your Documents, Perfectly Curated.
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    We are actively monitoring the expiration dates of your compliance certificates. You will receive an automated alert 60 days before any document expires.
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">
                      COMPLIANCE STATUS
                    </p>
                    <span className={`text-2xl font-bold ${compliancePct >= 80 ? 'text-[#00543b]' : compliancePct >= 50 ? 'text-orange-600' : 'text-red-600'}`}>
                      {compliancePct}% Secure
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Service History Tab */}
          {activeTab === 'service-history' && (
            <div>
              <Card className="border-gray-200">
                <CardContent className="p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-600 uppercase">
                            Order #
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-600 uppercase">
                            Service
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-600 uppercase">
                            Date
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-600 uppercase">
                            Status
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-600 uppercase">
                            Amount
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-600 uppercase">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {services.map((service) => (
                          <tr
                            key={service.id}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="py-4 px-4 text-sm text-gray-900 font-semibold">
                              {service.orderNumber}
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-900 font-semibold">
                              {service.service}
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-600">
                              {service.date}
                            </td>
                            <td className="py-4 px-4">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                                <Check className="h-3 w-3 mr-1" />
                                {service.status}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-900 font-semibold">
                              £{service.amount.toFixed(2)}
                            </td>
                            <td className="py-4 px-4">
                              <button
                                onClick={() => navigate(`/order/${service.id}`)}
                                className="text-[#00543b] text-sm font-semibold hover:underline"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Compliance Section */}
              <Card className="border-gray-200 mt-6">
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-900 mb-4">
                    Your Documents, Perfectly Curated.
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    We are actively monitoring the expiration dates of your compliance certificates. You will receive an automated alert 60 days before any document expires.
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">
                      COMPLIANCE STATUS
                    </p>
                    <span className={`text-2xl font-bold ${compliancePct >= 80 ? 'text-[#00543b]' : compliancePct >= 50 ? 'text-orange-600' : 'text-red-600'}`}>
                      {compliancePct}% Secure
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </DashboardLayout>
    </>
  );
};

export default PropertyDetailPage;
